import moment from 'moment-timezone';
import {Datum, PlotData} from 'plotly.js';

import {BarColors} from './colors';
import {Section as Query} from './query';
import {Section as _Config} from './types';

type Config = _Config['details']['historical']['chart'];

const groupByColor = (query: Query, config: Config, reverse?: boolean) => {
	const ages: number[] = [];

	const grouped: (Partial<PlotData> & {
		color: string,
		y: Datum[],
		hovertext: string[],
	})[] = [
		{
			color: BarColors.white, y: [],
			hoverinfo: 'skip',
			hovertext: [],
		},
		{
			color: BarColors.green, y: [],
			name: reverse ? 'High' : 'Low',
			hovertemplate: '%{hovertext}',
			hovertext: [],
		},
		{
			color: BarColors.yellow, y: [],
			name: 'Med',
			hovertemplate: '%{hovertext}',
			hovertext: [],
		},
		{
			color: BarColors.red, y: [],
			name: reverse ? 'Low' : 'High',
			hovertemplate: '%{hovertext}',
			hovertext: [],
		},
	];

	// Switch around our groups to change colors
	if (reverse) {
		const temp = grouped[1];
		grouped[1] = grouped[3];
		grouped[3] = temp;
	}

	// Concat historical breakpoints into values
	for (const hist of query.historicals) {
		if (hist.age <= 0) {
			continue;
		}
		ages.push(hist.age);

		for (let i = 0; i < grouped.length; i++) {
			const max = hist.breakpoints[i];
			const min = i > 0 ? hist.breakpoints[i-1] : 0;

			grouped[i].y.push(max <= (config.min || 0) ? null : max);
			grouped[i].hovertext.push(config.hover(min, max));
		}
	}

	return {
		ages,
		grouped: grouped.reverse(),
	};
};

export const dateify = (date: string): string => {
	return moment(date).format('MM/DD/YYYY');
};

export const useHistorical = (query: Query, config: Config, reverse?: boolean) => {
	const {ages, grouped} = groupByColor(query, config, reverse);
	const bars = grouped.map(({color, ...rest}): Partial<PlotData> => ({
		type: 'bar',
		marker: {
			color,
		},
		x: ages,
		...rest,
	}));

	const marker: Partial<PlotData> = {
		mode: 'lines+markers',
		marker: {
			color: BarColors.marker,
			size: 8,
		},
		line: {
			color: BarColors.line,
			shape: 'spline',
			dash: 'dashdot',
			width: 4,
		},
		name: 'Result',

		x: query.results.map((r) => r.age),
		y: query.results.map((r) => r.value),
		xaxis: 'x2',

		hovertemplate: config.marker,
		showlegend: false,
	};

	return {
		historical: {
			data: [marker, ...bars],
			ticks: {
				tickvals: query.results.map((r) => r.age),
				ticktext: query.results.map((r) => dateify(r.start.date)),
			},
			ylabel: config.ylabel,
			yrange: config.yrange,
			minAge: config.minAge,
			maxAge: config.maxAge
		},
	};
};
